import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output } from '@angular/core';
import { LucideAngularModule, LucideIconData } from 'lucide-angular';
import { TooltipDirective } from '../../../directives/tooltip.directive';

/** Supported visual treatments for the shared button component. */
export type ButtonVariant = 'primary' | 'secondary' | 'cancel' | 'danger' | 'ghost' | 'danger-ghost' | 'accent-ghost';
/** Supported button sizes. */
export type ButtonSize    = 'sm' | 'md' | 'lg';

/**
 * Shared button component with variants, icons, loading state, and ripple feedback.
 */
@Component({
  selector: 'app-button',
  imports: [LucideAngularModule, TooltipDirective],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './button.component.html',
  styleUrl: './button.component.css',
})
export class ButtonComponent {
  /** Visible text. Can be omitted when iconOnly is true. */
  readonly label        = input<string>('');
  /** Visual color/intent variant. */
  readonly variant      = input<ButtonVariant>('primary');
  /** Button sizing preset. */
  readonly size         = input<ButtonSize>('md');
  /** Optional Lucide icon rendered inside the button. */
  readonly icon         = input<LucideIconData | undefined>(undefined);
  /** Position for the icon when a label is also shown. */
  readonly iconPosition = input<'left' | 'right'>('left');
  /** Renders a square icon-only button (no label). */
  readonly iconOnly     = input<boolean>(false);
  /** Stretches the button to 100% width (useful for full-width list items). */
  readonly fullWidth    = input<boolean>(false);
  /** Shows the loading state and prevents interaction. */
  readonly loading      = input<boolean>(false);
  /** Disables the underlying button. */
  readonly disabled     = input<boolean>(false);
  /** Native button type. */
  readonly type         = input<'button' | 'submit'>('button');
  /** Optional tooltip shown for compact/icon-only usage. */
  readonly tooltip      = input<string>('');
  /** Optional full visual class override for theme-aware one-off buttons. */
  readonly customClass  = input<string>('');

  /** Emits when the button is clicked by the template. */
  readonly clicked = output<void>();

  /**
   * Adds a temporary ripple element for primary button clicks.
   *
   * @param event Mouse event used to position the ripple.
   */
  triggerRipple(event: MouseEvent): void {
    if (this.variant() !== 'primary' || this.disabled() || this.loading()) return;
    const btn = this.elRef.nativeElement.querySelector('button') as HTMLButtonElement;
    const rect = btn.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;
    const ripple = document.createElement('span');
    ripple.style.cssText = `
      position:absolute;border-radius:50%;pointer-events:none;
      width:${size}px;height:${size}px;left:${x}px;top:${y}px;
      background:rgb(var(--talos-bg-rgb) / 0.38);transform:scale(0);
      animation:ripple-anim 0.55s linear;
    `;
    btn.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove());
  }

  /** Host reference used to locate the native button element for ripple effects. */
  private readonly elRef = inject(ElementRef<HTMLElement>);

  /** Tailwind class map for each supported button variant. */
  private readonly VARIANTS: Record<ButtonVariant, string> = {
    'primary':      'btn-primary text-[var(--talos-bg)] border border-transparent uppercase tracking-[0.08em]',
    'secondary':    'bg-[var(--talos-bg)] text-talos-ink border border-talos-ink/18 hover:bg-talos-bg hover:border-talos-ink/28',
    'cancel':       'bg-[var(--talos-bg)] text-talos-ink border border-talos-ink/18 hover:bg-talos-bg',
    'danger':       'bg-red-600 text-[var(--talos-bg)] border-0 hover:bg-red-700',
    'ghost':        'bg-transparent text-talos-ink border border-transparent hover:bg-talos-ink/5',
    'danger-ghost': 'bg-transparent text-red-600 border-0 hover:bg-red-50',
    'accent-ghost': 'bg-transparent text-talos-ink/42 border border-transparent hover:bg-talos-accent/7 hover:text-talos-accent',
  };

  /** Full button class string derived from variant, size, width, and icon-only mode. */
  readonly classes = computed(() => {
    const base = 'inline-flex items-center justify-center gap-2 font-semibold cursor-pointer transition-all focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed';
    const variant = this.customClass() || this.VARIANTS[this.variant()];
    const width   = this.fullWidth() ? 'w-full' : '';

    if (this.iconOnly()) {
      const iconSizes: Record<ButtonSize, string> = {
        sm: 'w-7 h-7 rounded',
        md: 'w-9 h-9 rounded-md',
        lg: 'w-10 h-10 rounded-md',
      };
      return `${base} ${variant} ${iconSizes[this.size()]} ${width}`.trim();
    }

    const sizes: Record<ButtonSize, string> = {
      sm: 'px-3 py-1.5 text-[10px] rounded',
      md: 'px-4 py-2.5 text-xs rounded',
      lg: 'px-5 py-3 text-sm rounded',
    };
    return `${base} ${variant} ${sizes[this.size()]} ${width}`.trim();
  });

  /** Icon pixel size derived from the button size and layout mode. */
  readonly iconSize = computed(() => {
    if (this.iconOnly()) {
      return this.size() === 'sm' ? 14 : this.size() === 'lg' ? 20 : 17;
    }
    return this.size() === 'sm' ? 13 : this.size() === 'lg' ? 18 : 15;
  });
}
